import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { AccessionIdDisplay } from "../../../_lib/accession-id-display";
import {
  EPOCUS_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_URGENT_BG,
  EPOCUS_URGENT_TEXT,
  rem,
} from "../../../_lib/epocus-palette";
import { usePerformerName } from "../../../_lib/hooks/use-performer-name";
import { requestedExamTypeLabel } from "../../../_lib/scan-request/scan-request-display";
import {
  SettingsCard,
  SettingsCardLabel,
  SettingsCardLabelRow,
  SettingsScopeChip,
} from "../../_lib/settings-card";
import {
  type EpocusWorklistEntryFragment,
  useEpocusClearSeededWorklistMutation,
  useEpocusProgramWorklistQuery,
  useEpocusSeedWorklistMutation,
} from "./__generated__/program-config-operations.gql";

const SEED_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const HelperText: FCwC = ({ children }) => (
  <Typography style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}>
    {children}
  </Typography>
);

const EntryRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "10px 0",
  borderTop: `1px solid ${EPOCUS_BORDER}`,
});

const EntryTitleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
});

const EntryName = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
});

const UrgentTag = styled("span")({
  backgroundColor: EPOCUS_URGENT_BG,
  color: EPOCUS_URGENT_TEXT,
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: "0.05em",
  padding: "2px 6px",
  borderRadius: 4,
});

const ActionRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
});

const PerformerLabel: FC<{ performerId: string | null | undefined }> = ({
  performerId,
}) => {
  const { displayName } = usePerformerName(performerId);

  return (
    <HelperText>{performerId ? displayName : "Pool — unassigned"}</HelperText>
  );
};

const WorklistEntryRow: FC<{ entry: EpocusWorklistEntryFragment }> = ({
  entry,
}) => {
  const examLabel = requestedExamTypeLabel(entry.requestedExamType ?? null);

  return (
    <EntryRow>
      <EntryTitleRow>
        <EntryName>{entry.patientName ?? "Unknown patient"}</EntryName>
        {entry.requestPriority === "URGENT" && <UrgentTag>URGENT</UrgentTag>}
      </EntryTitleRow>
      <AccessionIdDisplay
        aria-label={`Accession ID ${entry.accessionNumber}`}
        style={{ alignSelf: "flex-start", flex: "none" }}
      >
        {entry.accessionNumber}
      </AccessionIdDisplay>
      <HelperText>
        {[examLabel, entry.indication].filter(Boolean).join(" · ")}
      </HelperText>
      <PerformerLabel performerId={entry.performerId} />
    </EntryRow>
  );
};

export const DemoWorklistCard: FC<{ programId: string }> = ({ programId }) => {
  const [count, setCount] = useState(5);
  const [seeded, setSeeded] =
    useState<ReadonlyArray<EpocusWorklistEntryFragment> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, refetch } = useEpocusProgramWorklistQuery({
    variables: { programId },
    fetchPolicy: "cache-and-network",
  });
  const [seedWorklist, { loading: seeding }] = useEpocusSeedWorklistMutation();
  const [clearWorklist, { loading: clearing }] =
    useEpocusClearSeededWorklistMutation();

  const openEntries = data?.epocusProgramWorklist ?? [];

  const onSeed = async () => {
    setError(null);
    try {
      const result = await seedWorklist({ variables: { programId, count } });
      setSeeded(result.data?.seedEpocusWorklist.entries ?? []);
      await refetch();
    } catch (seedError) {
      setError("Couldn't seed the worklist. Please try again.");
      Sentry.captureException(seedError);
    }
  };

  const onClear = async () => {
    setError(null);
    try {
      await clearWorklist({ variables: { programId } });
      setSeeded(null);
      await refetch();
    } catch (clearError) {
      setError("Couldn't clear the seeded requests. Please try again.");
      Sentry.captureException(clearError);
    }
  };

  return (
    <SettingsCard>
      <SettingsCardLabelRow>
        <SettingsCardLabel>Demo worklist</SettingsCardLabel>
        <SettingsScopeChip>Dev &amp; demo only</SettingsScopeChip>
      </SettingsCardLabelRow>
      <HelperText>
        These appear on any probe pointed at this program's worklist (MWL).
      </HelperText>

      <ActionRow>
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}>
          Seed
        </Typography>
        <Select
          variant="outlined"
          margin="dense"
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          inputProps={{ "aria-label": "Number of scan requests to seed" }}
          style={{ minWidth: 72 }}
        >
          {SEED_COUNTS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}>
          scan requests into the worklist
        </Typography>
      </ActionRow>

      <ActionRow>
        <Button
          variant="outlined"
          size="small"
          onClick={onSeed}
          disabled={seeding || clearing}
          style={{ textTransform: "none", fontWeight: 600 }}
          startIcon={
            seeding ? (
              <CircularProgress size={14} style={{ color: "inherit" }} />
            ) : undefined
          }
        >
          Seed scan requests
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onClear}
          disabled={seeding || clearing}
          style={{
            textTransform: "none",
            fontWeight: 600,
            color: EPOCUS_TEXT_SECONDARY,
            borderColor: EPOCUS_BORDER,
          }}
          startIcon={
            clearing ? (
              <CircularProgress size={14} style={{ color: "inherit" }} />
            ) : undefined
          }
        >
          Clear seeded requests
        </Button>
      </ActionRow>

      {error && (
        <Typography
          role="status"
          style={{ fontSize: rem(12), color: EPOCUS_URGENT_TEXT }}
        >
          {error}
        </Typography>
      )}

      {seeded && (
        <Box
          style={{
            backgroundColor: EPOCUS_SELECTED_BG,
            borderRadius: 8,
            padding: "4px 12px",
          }}
        >
          <HelperText>
            Created {seeded.length}{" "}
            {seeded.length === 1 ? "request" : "requests"}
          </HelperText>
          {seeded.map((entry) => (
            <WorklistEntryRow key={entry.encounterId} entry={entry} />
          ))}
        </Box>
      )}

      <Box>
        <SettingsCardLabel>Open worklist</SettingsCardLabel>
        <HelperText>
          Exactly what a probe sees when it queries this program.
        </HelperText>
        {openEntries.length === 0 ? (
          <HelperText>No open orders on the worklist.</HelperText>
        ) : (
          openEntries.map((entry) => (
            <WorklistEntryRow key={entry.encounterId} entry={entry} />
          ))
        )}
      </Box>
    </SettingsCard>
  );
};
